import React, {
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { renderToolTip } from "../../screens/portfolioApp/portfolioList/columnsDef";
import { Button } from "antd";
import { RetweetOutlined } from "@ant-design/icons";
import ReactDOMServer from "react-dom/server";

ChartJS.register(
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  zoomPlugin,
  ChartDataLabels
);

const getPinColor = (value) => {
  if (value === "" || value === undefined || value === null) return "#8884d8";
  const minValue = 60;
  const maxValue = 100;
  const normalized = (value - minValue) / (maxValue - minValue);
  const hue = 120 * normalized;
  return `hsl(${hue}, 100%, 50%)`;
};

const PerformanceBubbleChart = forwardRef(
  (
    {
      formattedData,
      dealerData,
      selectedTab,
      setSelectedRowKeys,
      selectedRowKeys,
      factorMapping,
      tab,
      currentView,
      activePortfolio,
      RooftopData,
    },
    ref
  ) => {
    const chartRef = useRef(null);
    const isDraggingRef = useRef(false);

    useImperativeHandle(ref, () => ({
      zoomIn: () => chartRef.current?.zoom(1.2),
      zoomOut: () => chartRef.current?.zoom(0.8),
      resetZoom: () => chartRef.current?.resetZoom(),
    }));

    const getFactorValue = (item) => {
      switch (selectedTab) {
        case "Performance":
          return item.Performance;
        case "Environment":
          return item.Environment;
        case "Opportunity":
          return item.Opportunity;
        case "Value":
          return item.Value;
        default:
          return;
      }
    };
    const previousZoomState = useRef(null);

    const preserveZoomState = () => {
      if (chartRef.current) {
        const chart = chartRef.current;
        previousZoomState.current = {
          xMin: chart.options.scales.x.min,
          xMax: chart.options.scales.x.max,
          yMin: chart.options.scales.y.min,
          yMax: chart.options.scales.y.max,
        };
      }
    };

    const restoreZoomState = () => {
      if (chartRef.current && previousZoomState.current) {
        const chart = chartRef.current;
        chart.options.scales.x.min = previousZoomState.current.xMin;
        chart.options.scales.x.max = previousZoomState.current.xMax;
        chart.options.scales.y.min = previousZoomState.current.yMin;
        chart.options.scales.y.max = previousZoomState.current.yMax;
        chart.update(); // ✅ This ensures the chart re-renders with the previous zoom level
      }
    };

    const previousDotPositions = useRef(new Map());

    const chartData = useMemo(() => {
      preserveZoomState();

      // For group view, use index as identifier; otherwise use JUMPID
      const isGroupView = currentView === "group";
      
      // Sort data appropriately based on view type
      const sortedData = isGroupView 
        ? [...formattedData] // Keep original order for group view
        : [...formattedData].sort((a, b) => a.JUMPID - b.JUMPID);

      return sortedData
        .filter((item) => item.Performance && item.Environment)
        .map((item, index) => {
          // For group view, use index; for rooftop/dealer, use JUMPID
          const itemId = isGroupView ? index : item.JUMPID;
          
          const dealerInfo = isGroupView
            ? RooftopData[index] || {} // For group view, match by index
            : RooftopData.find((dealer) => dealer.DealerID == item.JUMPID) || {};
          
          // For rooftop: use DealerRank, for group: use index + 1
          const dealerRank = isGroupView ? (index + 1).toString() : (dealerInfo.DealerRank || "");
          const isActive = isGroupView ? false : activePortfolio?.includes(item.JUMPID);
          const isNoneTab = selectedTab === "None";

          // Always update position with current values (for search/filter updates)
          previousDotPositions.current.set(itemId, {
            x: item.Environment,
            y: item.Performance,
          });

          // Use current values for position
          const x = item.Environment;
          const y = item.Performance;

          return {
            x,
            y,
            id: itemId,
            backgroundColor:
              isNoneTab && isActive
                ? "#5e93eb"
                : isGroupView
                ? getPinColor(getFactorValue(item)) // No selection for group view
                : selectedRowKeys?.includes(item.JUMPID)
                ? "#2369eb"
                : getPinColor(getFactorValue(item)),
            radius: 11,
            dealerRank,
          };
        });
    }, [formattedData, selectedTab, selectedRowKeys, activePortfolio, currentView, RooftopData]);

    const customTooltipHandler = (context) => {
      const tooltip = context.tooltip;
      
      // Hide tooltip if dragging
      let tooltipDiv = document.getElementById("chart-tooltip");
      if (isDraggingRef.current) {
        if (tooltipDiv) {
          tooltipDiv.style.opacity = "0";
          tooltipDiv.style.visibility = "hidden";
        }
        return;
      }
      
      if (
        !tooltip ||
        !tooltip.dataPoints ||
        tooltip.dataPoints.length === 0 ||
        !chartData ||
        chartData.length === 0
      ) {
        // Hide tooltip when not hovering over a data point
        if (tooltipDiv && tooltipDiv.dataset.mouseOver !== "true") {
          tooltipDiv.style.opacity = "0";
          tooltipDiv.style.visibility = "hidden";
        }
        return;
      }

      const chart = chartRef.current;
      const chartCanvas = chart.canvas;
      const chartRect = chartCanvas.getBoundingClientRect();

      // Get the data point index
      const dataIndex = tooltip.dataPoints[0]?.dataIndex;
      if (dataIndex === undefined) return;
      
      // Get the actual data values (x, y coordinates in data space)
      const dataPoint = chartData[dataIndex];
      if (!dataPoint) return;
      
      // Check if bubble is out of bounds (using the actual data values)
      const currentXMin = chart.scales.x.min;
      const currentXMax = chart.scales.x.max;
      const currentYMin = chart.scales.y.min;
      const currentYMax = chart.scales.y.max;
      
      // Hide tooltip if bubble is out of visible bounds
      if (dataPoint.x < currentXMin || dataPoint.x > currentXMax || 
          dataPoint.y < currentYMin || dataPoint.y > currentYMax) {
        if (tooltipDiv) {
          tooltipDiv.style.opacity = "0";
          tooltipDiv.style.visibility = "hidden";
        }
        return;
      }

      // Find dealer info
      const dealerId = chartData[dataIndex]?.id;
      const isGroupView = currentView === "group";
      let dealerInfo = isGroupView
        ? RooftopData[dealerId] || {} // For group view, use index
        : RooftopData.find((d) => d.DealerID == dealerId) || {};

      // Convert JSX to HTML string - use custom tooltip for group view
      let tooltipContent;
      if (isGroupView && dealerInfo) {
        // Custom group view tooltip - directly use group data
        const groupData = dealerInfo;
        
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        
        const formatRev = (rev) => {
          if (rev >= 1000000) return `$${(rev / 1000000).toFixed(2)}M`;
          return `$${(rev / 1000).toFixed(0)}K`;
        };
        
        // Define fields to display in tooltip with their formatting
        const tooltipFields = [
          { key: 'Performance', label: 'Performance', format: (val) => val.toFixed(2) },
          { key: 'Environment', label: 'Environment', format: (val) => val.toFixed(2) },
          { key: 'Opportunity', label: 'Opportunity', format: (val) => val.toFixed(2) },
          { key: 'Value', label: 'Value', format: (val) => val.toFixed(2) },
          { key: 'AvgMonthlyRevenue', label: 'Avg Mo Revenue', format: (val) => formatRev(val) },
          { key: 'PVRNew', label: 'PVR New', format: (val) => `$${Math.round(val).toLocaleString()}` },
          { key: 'F&INew', label: 'F&I New', format: (val) => `$${Math.round(val).toLocaleString()}` },
          { key: 'NewSales', label: 'New Sales', format: (val) => Math.round(val).toString() },
          { key: 'UsedSales', label: 'Used Sales', format: (val) => Math.round(val).toString() },
          { key: 'New/UsedRatio', label: 'New/Used Ratio', format: (val) => val.toFixed(2) },
        ];
        
        tooltipContent = ReactDOMServer.renderToString(
          <div style={{ fontSize: "12px", color: "black" }}>
            <div><b>{groupData.GroupName}</b></div>
            <div><b style={{ fontSize: "10px" }}>{groupData.City ? groupData.City + "," : ""} {groupData.State || ""}</b></div>
            <hr />
            <div>
              <div>[Monthly Averages based on prior 3 months]</div>
              <span><b>Valuation Range :</b> {formatter.format(groupData.LowValuation)} - {formatter.format(groupData.HighValuation)}</span>
              <br />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", lineHeight: "14px" }}>
              {tooltipFields.map(({ key, label, format }) => {
                const value = groupData[key];
                if (value !== undefined && value !== null) {
                  return <span key={key}><b>{label}</b>: {format(value)}</span>;
                }
                return null;
              })}
            </div>
            <div>
              <span><b>Rooftop Name(s):</b> {groupData.DealerName || ""}</span>
            </div>
          </div>
        );
      } else {
        tooltipContent = dealerInfo
          ? ReactDOMServer.renderToString(
              renderToolTip(dealerInfo, factorMapping)
            )
          : "Unknown Dealer";
      }

      if (!tooltipDiv) {
        tooltipDiv = document.createElement("div");
        tooltipDiv.id = "chart-tooltip";
        document.body.appendChild(tooltipDiv);

        // Add mouse event listeners to the tooltip
        tooltipDiv.addEventListener("mouseenter", () => {
          tooltipDiv.dataset.mouseOver = "true";
        });

        tooltipDiv.addEventListener("mouseleave", () => {
          tooltipDiv.dataset.mouseOver = "false";
          tooltipDiv.style.opacity = "0";
          tooltipDiv.style.visibility = "hidden";
        });
      }

      tooltipDiv.innerHTML = tooltipContent;
      // Reset scroll position when showing new tooltip
      tooltipDiv.scrollTop = 0;

      // Get correct x and y from the active data point
      const tooltipPoint = tooltip.dataPoints[0];
      if (!tooltipPoint) return;

      const { x, y } = chart
        .getDatasetMeta(0)
        .data[tooltipPoint.dataIndex].getProps(["x", "y"]);

      // Calculate tooltip position with offset from the bubble
      const tooltipOffset = 3;
      const tooltipX = chartRect.left + window.scrollX + x;
      const tooltipY = chartRect.top + window.scrollY + y;

      // Get tooltip dimensions
      const tooltipWidth = 280; // Width we set for tooltip
      const tooltipHeight = tooltipDiv.offsetHeight;

      // Get current viewport of the chart
      const viewport = {
        left: chart.options.scales.x.min,
        right: chart.options.scales.x.max,
        top: chart.options.scales.y.min,
        bottom: chart.options.scales.y.max,
      };

      // Calculate available space
      const spaceAbove = y;
      const spaceBelow = chartRect.height - y;
      const spaceLeft = x;
      const spaceRight = chartRect.width - x;

      // Position tooltip
      tooltipDiv.style.position = "absolute";
      
      // Determine if bubble is in the right half of the chart
      const isInRightHalf = x > chartRect.width / 2;
      
      // Adjust horizontal position
      if (isInRightHalf) {
        // For bubbles on right half, position tooltip to the left
        tooltipDiv.style.left = `${tooltipX - tooltipWidth - tooltipOffset}px`;
      } else {
        // For bubbles on left half, position tooltip to the right
        tooltipDiv.style.left = `${tooltipX + tooltipOffset}px`;
      }

      // Adjust vertical position
      if (spaceBelow < tooltipHeight) {
        // Not enough space below, show above
        tooltipDiv.style.top = `${tooltipY - tooltipHeight - tooltipOffset}px`;
      } else {
        // Show below with offset to keep bubble visible
        tooltipDiv.style.top = `${tooltipY + tooltipOffset}px`;
      }

      tooltipDiv.style.transform = "none"; // Remove the transform since we're handling positioning manually
      tooltipDiv.style.backgroundColor = "white";
      tooltipDiv.style.padding = "12px";
      tooltipDiv.style.pointerEvents = "auto"; // Changed to auto to make it interactive
      tooltipDiv.style.zIndex = "5";
      tooltipDiv.style.boxShadow = "0px 4px 6px rgba(0,0,0,0.1)";
      tooltipDiv.style.width = "280px";
      tooltipDiv.style.maxHeight = "300px";
      tooltipDiv.style.overflowY = "auto";
      tooltipDiv.style.borderRadius = "6px";
      tooltipDiv.style.fontSize = "14px";
      tooltipDiv.style.lineHeight = "1.4";
      tooltipDiv.style.cursor = "default";

      // Show tooltip
      tooltipDiv.style.opacity = "1";
      tooltipDiv.style.visibility = "visible";
    };

    useEffect(() => {
      restoreZoomState();
    }, [chartData]);

    useEffect(() => {
      if (chartRef.current) {
        chartRef.current.resetZoom();
      }
    }, [tab]);

    // Add cleanup for tooltip
    useEffect(() => {
      return () => {
        const tooltipDiv = document.getElementById("chart-tooltip");
        if (tooltipDiv) {
          tooltipDiv.remove();
        }
      };
    }, []);

    const data = {
      datasets: [
        {
          label: "Performance vs Environment",
          data: chartData,
          backgroundColor: chartData.map((d) => d.backgroundColor),
          pointRadius: chartData.map((d) => d.radius),
          pointHoverRadius: chartData.map((d) => d.radius),
          datalabels: {
            color: "black",
            font: {
              weight: "bold",
              size: 12,
            },
            align: "center",
            formatter: (value, context) => {
              return chartData[context.dataIndex].dealerRank || "";
            },
          },
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      onHover: (event, elements) => {
        const tooltipDiv = document.getElementById("chart-tooltip");
        if (!elements || elements.length === 0) {
          if (tooltipDiv && tooltipDiv.dataset.mouseOver !== "true") {
            tooltipDiv.style.opacity = "0";
            tooltipDiv.style.visibility = "hidden";
          }
        }
      },
      events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove', 'mousedown', 'mouseup'],
      onEvent: (chart, event) => {
        if (event.native.type === 'mousedown' || event.native.type === 'touchstart') {
          isDraggingRef.current = true;
          const tooltipDiv = document.getElementById("chart-tooltip");
          if (tooltipDiv) {
            tooltipDiv.style.opacity = "0";
            tooltipDiv.style.visibility = "hidden";
          }
        } else if (event.native.type === 'mouseup' || event.native.type === 'touchend') {
          // Small delay to prevent tooltip from showing immediately after drag
          setTimeout(() => {
            isDraggingRef.current = false;
          }, 100);
        }
      },
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          title: {
            display: true,
            text: "Environment",
            font: {
              size: 16,
              weight: "bold",
            },
            color: "#333",
          },
          min: 60,
          max: 100,
          ticks: {
            stepSize: 20,
          },
        },
        y: {
          type: "linear",
          title: {
            display: true,
            text: "Performance",
            font: {
              size: 16,
              weight: "bold",
            },
            color: "#333",
          },
          min: 60,
          max: 100,
          ticks: {
            stepSize: 20,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context) => {
            // Only show tooltip when hovering over a data point
            if (
              context.tooltip.dataPoints &&
              context.tooltip.dataPoints.length > 0
            ) {
              customTooltipHandler(context);
            } else {
              // Hide tooltip when not hovering over a data point
              const tooltipDiv = document.getElementById("chart-tooltip");
              if (tooltipDiv && tooltipDiv.dataset.mouseOver !== "true") {
                tooltipDiv.style.opacity = "0";
                tooltipDiv.style.visibility = "hidden";
              }
            }
          },
        },
        datalabels: {
          display: true,
          clip: true,
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "xy",
            onPanStart: () => {
              isDraggingRef.current = true;
              const tooltipDiv = document.getElementById("chart-tooltip");
              if (tooltipDiv) {
                tooltipDiv.style.opacity = "0";
                tooltipDiv.style.visibility = "hidden";
              }
            },
            onPan: () => {
              // Keep tooltip hidden during pan
              const tooltipDiv = document.getElementById("chart-tooltip");
              if (tooltipDiv) {
                tooltipDiv.style.opacity = "0";
                tooltipDiv.style.visibility = "hidden";
              }
            },
            onPanComplete: () => {
              isDraggingRef.current = false;
            },
          },
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.05,
            },
            pinch: {
              enabled: true,
            },
            mode: "xy",
            onZoomStart: () => {
              isDraggingRef.current = true;
              const tooltipDiv = document.getElementById("chart-tooltip");
              if (tooltipDiv) {
                tooltipDiv.style.opacity = "0";
                tooltipDiv.style.visibility = "hidden";
              }
            },
            onZoom: () => {
              // Keep tooltip hidden during zoom
              const tooltipDiv = document.getElementById("chart-tooltip");
              if (tooltipDiv) {
                tooltipDiv.style.opacity = "0";
                tooltipDiv.style.visibility = "hidden";
              }
            },
            onZoomComplete: () => {
              isDraggingRef.current = false;
            },
            limits: {
              x: { min: 55, max: 105 },
              y: { min: 55, max: 105 },
              maxZoom: 2,
              minZoom: 0.5,
            },
            scaleMode: "xy",
          },
        },
        legend: {
          display: false,
        },
      },

      onClick: (event, elements) => {
        if (tab === "active" || currentView == "group") return;
        if (elements.length > 0 && currentView !== "dealer") {
          const index = elements[0].index;
          const dealerId = chartData[index].id;

          if (activePortfolio?.includes(dealerId)) return;
          setSelectedRowKeys((prev) =>
            prev.includes(dealerId)
              ? prev.filter((id) => id !== dealerId)
              : [...prev, dealerId]
          );
        }
      },
    };

    return (
      <div style={{ width: "1000px", height: "550px" }}>
        <div style={{ textAlign: "center" }}>Performance vs Environment</div>{" "}
        {/* ✅ Set container size */}
        <Scatter ref={chartRef} data={data} options={options} />
      </div>
    );
  }
);

export default PerformanceBubbleChart;
